import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  User, Building2, Shield, Bell, LogOut, Save, Mail, Key, Smartphone, ArrowLeft, Activity, Server,
} from 'lucide-react';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import {
  updatePassword, EmailAuthProvider, reauthenticateWithCredential,
} from 'firebase/auth';
import { useFamilyOffice } from '../contexts/FamilyOfficeContext';
import { OWNER_EMAIL } from '../lib/permissions';

const SECTIONS = [
  { id: 'account', label: 'Account', icon: User },
  { id: 'office', label: 'Family Office', icon: Building2 },
  { id: 'access', label: 'Access', icon: Shield },
  { id: 'notifications', label: 'Notifications', icon: Bell },
];

export default function Settings({ user, businesses, logout }) {
  const [activeSection, setActiveSection] = useState('account');
  const isOwner = user?.email === OWNER_EMAIL;

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="header-left">
          <Link to="/" className="btn btn-ghost" title="Back">
            <ArrowLeft size={16} /> Back
          </Link>
          <h1 className="app-title" style={{ marginLeft: 12 }}>Settings</h1>
        </div>
        <div className="header-right">
          <span className="user-email">{user.email}</span>
        </div>
      </header>

      <main className="settings-main">
        <aside className="settings-sidebar">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                className={`settings-nav-item ${activeSection === s.id ? 'active' : ''}`}
                onClick={() => setActiveSection(s.id)}
              >
                <Icon size={16} /> <span>{s.label}</span>
              </button>
            );
          })}
          <div className="settings-nav-divider" />
          <Link to="/activity" className="settings-nav-item">
            <Activity size={16} /> <span>Activity Feed</span>
          </Link>
          {isOwner && (
            <Link to="/infrastructure" className="settings-nav-item">
              <Server size={16} /> <span>Infrastructure</span>
            </Link>
          )}
          <button className="settings-nav-item settings-logout" onClick={logout}>
            <LogOut size={16} /> <span>Sign Out</span>
          </button>
        </aside>

        <section className="settings-content">
          {activeSection === 'account' && <AccountSection user={user} businesses={businesses} />}
          {activeSection === 'office' && <FamilyOfficeSection user={user} />}
          {activeSection === 'access' && <AccessSection isOwner={isOwner} />}
          {activeSection === 'notifications' && <NotificationsSection user={user} />}
        </section>
      </main>
    </div>
  );
}

// ── Account ──

function AccountSection({ user, businesses }) {
  const { activeFo, accessibleFos } = useFamilyOffice();
  const [profile, setProfile] = useState({ displayName: '', avatar_url: '' });
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Derive role: check if owner of any business in active FO, otherwise first role found
  const currentRole = useMemo(() => {
    if (user.email === OWNER_EMAIL) return 'Owner (super-admin)';
    const inFo = businesses.filter((b) => !activeFo || b.family_office_id === activeFo.id);
    const owner = inFo.find((b) => b.roles?.[user.uid] === 'owner');
    if (owner) return 'Owner';
    const any = inFo.find((b) => b.roles?.[user.uid]);
    return any ? any.roles[user.uid].replace('_', ' ') : 'No roles';
  }, [businesses, user, activeFo]);

  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, 'users', user.uid));
      const data = snap.exists() ? snap.data() : {};
      setProfile({
        displayName: data.displayName || '',
        avatar_url: data.avatar_url || '',
      });
      setLoading(false);
    })();
  }, [user.uid]);

  const saveProfile = async () => {
    setSavingProfile(true);
    await setDoc(doc(db, 'users', user.uid), {
      displayName: profile.displayName,
      avatar_url: profile.avatar_url,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    setSavingProfile(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (loading) return <p>Loading...</p>;

  return (
    <>
      <h2>Account</h2>

      <div className="settings-card">
        <h3>Profile</h3>
        <label className="settings-field">
          <span>Display name</span>
          <input
            value={profile.displayName}
            onChange={(e) => setProfile({ ...profile, displayName: e.target.value })}
            placeholder="Kai"
          />
        </label>
        <label className="settings-field">
          <span>Email</span>
          <input value={user.email} disabled />
        </label>
        <label className="settings-field">
          <span>Avatar URL</span>
          <input
            value={profile.avatar_url}
            onChange={(e) => setProfile({ ...profile, avatar_url: e.target.value })}
            placeholder="https://..."
          />
        </label>
        <label className="settings-field">
          <span>Role</span>
          <div className="settings-readonly">
            <strong>{currentRole}</strong>
            {activeFo && ` in ${activeFo.short_code || activeFo.name}`}
          </div>
        </label>
        <div className="settings-actions">
          <button className="btn btn-primary" onClick={saveProfile} disabled={savingProfile}>
            <Save size={14} /> {savingProfile ? 'Saving…' : (saved ? 'Saved ✓' : 'Save profile')}
          </button>
        </div>
      </div>

      <ChangePasswordCard user={user} />

      <TwoFactorCard user={user} />
    </>
  );
}

function ChangePasswordCard({ user }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setOk(false);
    if (next !== confirm) { setErr('Passwords do not match'); return; }
    if (next.length < 8) { setErr('New password must be ≥ 8 characters'); return; }

    setSaving(true);
    try {
      const cred = EmailAuthProvider.credential(user.email, current);
      await reauthenticateWithCredential(auth.currentUser, cred);
      await updatePassword(auth.currentUser, next);
      setOk(true);
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e) {
      setErr(e.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-card">
      <h3>Password</h3>
      <form onSubmit={submit}>
        <label className="settings-field">
          <span>Current password</span>
          <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
        </label>
        <label className="settings-field">
          <span>New password</span>
          <input type="password" required value={next} onChange={(e) => setNext(e.target.value)} />
        </label>
        <label className="settings-field">
          <span>Confirm new password</span>
          <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </label>
        {err && <div className="settings-error">{err}</div>}
        {ok && <div className="settings-success">Password updated.</div>}
        <div className="settings-actions">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            <Key size={14} /> {saving ? 'Updating…' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  );
}

function TwoFactorCard({ user }) {
  const enrolled = auth.currentUser?.multiFactor?.enrolledFactors?.length > 0;

  return (
    <div className="settings-card">
      <h3>Two-Factor Authentication (2FA)</h3>
      <p className="settings-muted">
        Adds a second factor (phone SMS or TOTP app) on top of your password.
        Recommended for accounts with access to finance data.
      </p>
      <div className="settings-field">
        <span>Status</span>
        <div className="settings-readonly">
          {enrolled ? <strong style={{ color: '#5b8b6c' }}>Enabled</strong> : <strong style={{ color: '#b54a4a' }}>Disabled</strong>}
        </div>
      </div>
      <div className="settings-actions">
        <button className="btn btn-primary" disabled>
          <Smartphone size={14} /> {enrolled ? 'Manage 2FA' : 'Enable 2FA'} (coming soon)
        </button>
      </div>
      <p className="settings-hint">
        Phone-based 2FA requires reCAPTCHA setup; TOTP needs a Cloud Function. Wiring in a follow-up.
      </p>
    </div>
  );
}

// ── Family Office ──

function FamilyOfficeSection({ user }) {
  const { accessibleFos, activeFo, setActiveFoId, familyOffices } = useFamilyOffice();
  const [editingFoId, setEditingFoId] = useState(activeFo?.id || accessibleFos[0]?.id || null);
  const editingFo = accessibleFos.find((f) => f.id === editingFoId) || null;

  const [foForm, setFoForm] = useState({ name: '', default_currency: '', module_business: '', module_office: '' });
  const [defaultFoId, setDefaultFoId] = useState('');
  const [savedFo, setSavedFo] = useState(false);
  const [savedPrefs, setSavedPrefs] = useState(false);

  // If accessibleFos loads after this component mounts, hydrate editingFoId.
  useEffect(() => {
    if (!editingFoId && accessibleFos.length > 0) {
      setEditingFoId(activeFo?.id || accessibleFos[0].id);
    }
  }, [accessibleFos, activeFo, editingFoId]);

  useEffect(() => {
    if (!editingFo) return;
    setFoForm({
      name: editingFo.name || '',
      default_currency: editingFo.default_currency || 'SGD',
      module_business: editingFo.module_names?.business || 'Business',
      module_office: editingFo.module_names?.office || 'Office',
    });
  }, [editingFo]);

  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, 'users', user.uid));
      const data = snap.exists() ? snap.data() : {};
      setDefaultFoId(data.settings?.default_fo_id || '');
    })();
  }, [user.uid]);

  const saveFo = async () => {
    if (!editingFo) return;
    await updateDoc(doc(db, 'family_offices', editingFo.id), {
      name: foForm.name,
      default_currency: foForm.default_currency,
      module_names: { business: foForm.module_business, office: foForm.module_office },
    });
    setSavedFo(true);
    setTimeout(() => setSavedFo(false), 2000);
  };

  const savePrefs = async () => {
    await setDoc(doc(db, 'users', user.uid), {
      settings: { default_fo_id: defaultFoId || null },
    }, { merge: true });
    setSavedPrefs(true);
    setTimeout(() => setSavedPrefs(false), 2000);
  };

  if (!editingFo) {
    if (accessibleFos.length === 0 && familyOffices.length === 0) {
      return <p style={{ color: 'var(--nk-fog)' }}>Loading family offices…</p>;
    }
    return <p>No Family Offices accessible.</p>;
  }

  return (
    <>
      <h2>Family Office</h2>

      <div className="settings-card">
        <h3>Your preferences</h3>
        <label className="settings-field">
          <span>Default FO on sign-in</span>
          <select value={defaultFoId} onChange={(e) => setDefaultFoId(e.target.value)}>
            <option value="">(remember last used)</option>
            {accessibleFos.map((fo) => (
              <option key={fo.id} value={fo.id}>{fo.short_code || fo.name}</option>
            ))}
          </select>
        </label>
        <div className="settings-actions">
          <button className="btn btn-primary" onClick={savePrefs}>
            <Save size={14} /> {savedPrefs ? 'Saved ✓' : 'Save preference'}
          </button>
        </div>
      </div>

      <div className="settings-card">
        <h3>Per-FO settings</h3>
        <label className="settings-field">
          <span>Editing</span>
          <select value={editingFoId} onChange={(e) => setEditingFoId(e.target.value)}>
            {accessibleFos.map((fo) => (
              <option key={fo.id} value={fo.id}>{fo.short_code || fo.name}</option>
            ))}
          </select>
        </label>
        <label className="settings-field">
          <span>FO name</span>
          <input value={foForm.name} onChange={(e) => setFoForm({ ...foForm, name: e.target.value })} />
        </label>
        <label className="settings-field">
          <span>Default currency</span>
          <select value={foForm.default_currency} onChange={(e) => setFoForm({ ...foForm, default_currency: e.target.value })}>
            {['SGD', 'USD', 'EUR', 'GBP', 'JPY', 'HKD', 'THB', 'AUD'].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>

        <h4 style={{ marginTop: 16, marginBottom: 8 }}>Module names</h4>
        <label className="settings-field">
          <span>"Business" label</span>
          <input value={foForm.module_business} onChange={(e) => setFoForm({ ...foForm, module_business: e.target.value })} placeholder="Business" />
        </label>
        <label className="settings-field">
          <span>"Office" label</span>
          <input value={foForm.module_office} onChange={(e) => setFoForm({ ...foForm, module_office: e.target.value })} placeholder="Office" />
        </label>

        <div className="settings-actions">
          <button className="btn btn-primary" onClick={saveFo}>
            <Save size={14} /> {savedFo ? 'Saved ✓' : 'Save FO settings'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Access ──

function AccessSection({ isOwner }) {
  return (
    <>
      <h2>Access</h2>
      {isOwner ? (
        <div className="settings-card">
          <h3>Access management</h3>
          <p className="settings-muted">
            Manage which users can see which FOs, businesses, and sub-modules. Includes custom roles.
          </p>
          <div className="settings-actions">
            <Link to="/access" className="btn btn-primary">
              <Shield size={14} /> Open Access management
            </Link>
          </div>
        </div>
      ) : (
        <div className="settings-card">
          <h3>Access management</h3>
          <p className="settings-muted">Owner-only. Contact the account owner to change your access.</p>
        </div>
      )}
    </>
  );
}

// ── Notifications ──

function NotificationsSection({ user }) {
  const [prefs, setPrefs] = useState({
    email_digest: 'weekly',
    alerts: {
      bank_unmatched: true,
      low_health: true,
      low_runway: true,
      breakeven_warning: true,
      revenue_decline: true,
      pending_invites: true,
    },
  });
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, 'users', user.uid));
      const data = snap.exists() ? snap.data() : {};
      if (data.settings?.notifications) {
        setPrefs({
          email_digest: data.settings.notifications.email_digest || 'weekly',
          alerts: { ...prefs.alerts, ...(data.settings.notifications.alerts || {}) },
        });
      }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [user.uid]);

  const save = async () => {
    await setDoc(doc(db, 'users', user.uid), {
      settings: { notifications: prefs },
    }, { merge: true });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (loading) return <p>Loading...</p>;

  const ALERT_LABELS = {
    bank_unmatched: 'Bank — unmatched items',
    low_health: 'Business — low health score',
    low_runway: 'Business — low runway',
    breakeven_warning: 'Business — below breakeven',
    revenue_decline: 'Business — revenue decline',
    pending_invites: 'Team — pending invites',
  };

  return (
    <>
      <h2>Notifications</h2>

      <div className="settings-card">
        <h3>Email digest</h3>
        <label className="settings-field">
          <span>Frequency</span>
          <select value={prefs.email_digest} onChange={(e) => setPrefs({ ...prefs, email_digest: e.target.value })}>
            <option value="off">Off</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </label>
      </div>

      <div className="settings-card">
        <h3>Alert types</h3>
        <p className="settings-muted">Pick which alerts you want to receive.</p>
        {Object.keys(ALERT_LABELS).map((key) => (
          <label key={key} className="settings-check">
            <input
              type="checkbox"
              checked={prefs.alerts[key] || false}
              onChange={(e) => setPrefs({ ...prefs, alerts: { ...prefs.alerts, [key]: e.target.checked } })}
            />
            <span>{ALERT_LABELS[key]}</span>
          </label>
        ))}
      </div>

      <div className="settings-actions">
        <button className="btn btn-primary" onClick={save}>
          <Save size={14} /> {saved ? 'Saved ✓' : 'Save preferences'}
        </button>
      </div>
    </>
  );
}
